/**
 * Bottom Navigation Component
 * Mobile-first bottom navigation bar with sliding active indicator animation
 */

import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { HomeIcon, BudgetIcon, ReportIcon, ProfileIcon, WalletIcon } from '../icons/CategoryIcons';
import { useUIStore } from '../../store/uiStore';

export type Page = 'home' | 'balance' | 'budget' | 'receitas' | 'reports' | 'profile';

interface BottomNavProps {
  currentPage: Page;
  onNavigate: (page: Page) => void;
}

interface NavItemConfig {
  id: Page;
  icon: React.ReactNode;
  labelKey: string;
  testId: string;
}

// Spring transition for snappy, slightly bouncy feel
const springTransition = {
  type: 'spring' as const,
  stiffness: 400,
  damping: 30,
};

export function BottomNav({ currentPage, onNavigate }: BottomNavProps) {
  const { t } = useTranslation();
  const { theme } = useUIStore();
  const isDark = theme === 'dark';

  // Navigation items configuration
  const navItems: NavItemConfig[] = [
    { id: 'home', icon: <HomeIcon className="w-6 h-6" />, labelKey: 'nav.home', testId: 'nav-home' },
    { id: 'budget', icon: <BudgetIcon className="w-6 h-6" />, labelKey: 'nav.expenses', testId: 'nav-gastos' },
    { id: 'receitas', icon: <WalletIcon className="w-6 h-6" />, labelKey: 'nav.income', testId: 'nav-receitas' },
    { id: 'reports', icon: <ReportIcon className="w-6 h-6" />, labelKey: 'nav.reports', testId: 'nav-reports' },
    { id: 'profile', icon: <ProfileIcon className="w-6 h-6" />, labelKey: 'nav.profile', testId: 'nav-profile' },
  ];

  return (
    <nav
      className={`fixed bottom-0 left-0 right-0 backdrop-blur-md border-t safe-area-bottom transition-colors duration-200 ${
        isDark
          ? 'bg-slate-900/95 border-slate-800'
          : 'bg-white/95 border-slate-200'
      }`}
    >
      <div className="flex items-center justify-around max-w-lg mx-auto">
        {navItems.map((item) => {
          const isActive = currentPage === item.id;

          return (
            <button
              key={item.id}
              onClick={() => onNavigate(item.id)}
              data-testid={item.testId}
              className="relative flex flex-col items-center justify-center flex-1 py-2 min-h-[56px] min-w-[44px]"
            >
              {/* Icon container with sliding indicator */}
              <div className="relative p-1.5">
                {/* Sliding Active Indicator - uses layoutId for automatic animation */}
                <AnimatePresence>
                  {isActive && (
                    <motion.div
                      layoutId="active-tab-indicator"
                      className="absolute inset-0 bg-orange-500/20 rounded-lg"
                      initial={false}
                      transition={springTransition}
                    />
                  )}
                </AnimatePresence>

                {/* Icon - sits on top of the indicator */}
                <motion.div
                  className="relative z-10"
                  animate={{
                    color: isActive ? '#f97316' : isDark ? '#94a3b8' : '#64748b',
                  }}
                  transition={{ duration: 0.2 }}
                >
                  {item.icon}
                </motion.div>
              </div>

              {/* Label with color transition */}
              <motion.span
                className="text-[10px] mt-1 font-medium"
                animate={{
                  color: isActive ? '#f97316' : isDark ? '#94a3b8' : '#64748b',
                }}
                transition={{ duration: 0.2 }}
              >
                {t(item.labelKey)}
              </motion.span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
